import { consecutiveHourlyWindows, uniqueOrderedHours } from "./hourly-windows";
import { leafFormulaRevision } from "./engines/core/formula";
import { classifyModelFog } from "./model-fog";
// =============================================================================
// PeakWave - Sailing Conditions Scoring Model
// =============================================================================
//
// Scores dinghy sailing conditions on a 0-100 scale using hourly weather and
// marine data from Open-Meteo. Designed for UK/European club and competitive
// dinghy sailing. Two boat categories: Club Dinghy and High Performance.
//
// Architecture (multiplicative gate x additive quality, matching the platform's
// penalty-chain convention):
//
//   composite = windViability x gustStability^0.45 x quality x visibility^0.30
//
//   windViability  (0-1)  Gate from sustained wind vs a per-category curve.
//                         No wind means no sailing: the gate, not suppression,
//                         is what makes a becalmed day score 0-2.
//   gustStability  (0-1)  Power factor from the gust-ratio sub-score. Unstable
//                         wind is a viability problem (race officers postpone
//                         for gusting-double just as they do for calm).
//   quality        0-100  Additive comfort/quality composite:
//                           Sea State          0.40
//                           Precipitation      0.25
//                           Wind Consistency   0.22
//                           Pressure Trend     0.13
//   visibility     (0-1)  Power factor so fog has real bite.
//
//   Thermal Exposure is computed for display but excluded from the score by
//   design (permanent geographic bias; sailors dress for climate).
//   Storm Risk is likewise computed for display but excluded from the score
//   (storm-policy unification): lightning is a safety call, and platform
//   policy keeps safety in the flag layer. computeSailingSafety() returns
//   the red/amber indicators; storm rain still costs points via the
//   precipitation sub-score in millimetres like any other rain.
//
// Daily rollup: 70% best 4-hour window + 30% average across racing hours
// (09:00-18:00, daylight only), the platform-standard window-heavy blend.

import { composeScore, defineChain } from './compose-score';

// Sailing's per-window chain, declared as data (tracker P3-19); classes are
// enforced by composeScore. This is the documented hybrid exemption
// (audit 92.1): the additive quality core sits inside a multiplicative
// wrapper, entering the chain as a single opaque term. Only the 4-term
// per-window product routes through composeScore; the additive interior,
// the suppression override, the per-window max and the 70/30 daily blend
// stay in scoreSailing.
export const SAILING_WINDOW_CHAIN = defineChain({
  sport: 'sailing-window',
  // P6-1 documented exemption from the all-or-none gamma policy: this is not a
  // standard multiplicative quality chain. Its quality core is ADDITIVE and
  // enters as one opaque 0-100 term, so the four-term wrapper does not stack the
  // chain-length artefact gamma corrects (its own all-0.9 day already reads ~6.6
  // Great). Gamma would distort the calibrated additive composite. Null is kept
  // deliberately, not by omission.
  gamma: null,
  factors: [
    { key: 'windViability', class: 'driver', note: 'no wind, no sailing: gate-shaped driver' },
    { key: 'gustStability', class: 'driver' },
    { key: 'quality', class: 'driver', note: 'additive quality core (sea state, precipitation, wind consistency, pressure trend) enters as one opaque term: the documented hybrid exemption' },
    { key: 'visibility', class: 'driver', note: "vis power factor: tier-migrated quality (10 B3, mid-band ~0.85, deeper only in real fog), not a bounded enjoyment modifier, so not floored (P7-1). The separate fog hard-zero suppression is P7-10. Consistent with sailing's documented hybrid exemption: it carries no bounded modifier" },
  ],
});

export type BoatCategory = 'club' | 'high_performance';
export type VenueType = 'inland' | 'estuary' | 'coastal_open';
export type SailingLabel = 'Epic' | 'Firing' | 'Great' | 'Good' | 'Fair' | 'Poor' | 'Becalmed' | 'Insufficient data';

export interface SailingSafetyIndicator {
  level: 'amber' | 'red';
  label: string;
  detail: string;
  boatCategory?: BoatCategory;
}

export interface HourlyDatum {
  hour: number;
  wind_speed_10m_kmh: number;
  wind_dir_10m_deg: number;
  wind_gust_10m_kmh: number;
  wind_speed_80m_kmh?: number | null;
  wind_dir_80m_deg?: number | null;
  temp_c: number;
  apparent_temp_c: number;
  humidity_pct: number;
  rain_mm: number;
  precip_mm: number;
  precip_probability_pct?: number | null;
  weather_code: number;
  pressure_msl_hpa: number;
  visibility_m: number;
  cloud_cover_pct: number;
  is_day: number;
  cape_jkg: number;
  lifted_index: number;
  wave_height_m?: number | null;
  wave_dir_deg?: number | null;
  wave_period_s?: number | null;
  wind_wave_height_m?: number | null;
  wind_wave_dir_deg?: number | null;
  wind_wave_period_s?: number | null;
  swell_height_m?: number | null;
  swell_dir_deg?: number | null;
  swell_period_s?: number | null;
  swell_peak_period_s?: number | null;
  ocean_current_velocity_ms?: number | null;
  ocean_current_dir_deg?: number | null;
  sst_c?: number | null;
  source_visibility_m?: number | null;
  source_wave_height_m?: number | null;
  source_pressure_msl_hpa?: number | null;
  source_weather_code?: number | null;
  source_cape_jkg?: number | null;
  source_lifted_index?: number | null;
}

export interface SubScores {
  windQuality: number;
  gustFactor: number;
  windConsistency: number;
  seaState: number;
  thermalExposure: number;
  precipitation: number;
  visibility: number;
  stormRisk: number;
  pressureTrend: number;
}

export interface SailingScore {
  score: number;
  label: SailingLabel;
  boatCategory: BoatCategory;
  venueType: VenueType;
  /** End is exclusive, matching planner hourly intervals. */
  bestWindow: { startHour: number; endHour: number };
  subScores: SubScores;
  scoreFactors: Readonly<{
    windViability: number;
    gustStability: number;
    qualityPoints: number;
    visibilityMultiplier: number;
  }>;
  qualityContributions: Readonly<{
    windConsistency: number;
    seaState: number;
    precipitation: number;
    pressureTrend: number;
  }>;
  suppressed: boolean;
  suppressionReason: string | null;
  safety: SailingSafetyIndicator[];
}

const EMPTY_SCORE_FACTORS = Object.freeze({
  windViability: 0,
  gustStability: 0,
  qualityPoints: 0,
  visibilityMultiplier: 0,
});
const EMPTY_QUALITY_CONTRIBUTIONS = Object.freeze({
  windConsistency: 0,
  seaState: 0,
  precipitation: 0,
  pressureTrend: 0,
});

const KMH_TO_KNOTS = 0.539957;
const WINDOW_SIZE = 4;

// Racing hours: club and championship racing runs inside this band. The daily
// rollup only considers windows here (falling back to all daylight at high
// latitudes where the two barely overlap).
const RACING_HOURS_START = 9;
const RACING_HOURS_END = 18;

// Daily rollup blend, matching the platform-wide window-heavy convention
// (surf/bodyboard/SUP use the same 70/30 split).
const BEST_WINDOW_WEIGHT = 0.7;
const POOL_AVG_WEIGHT = 0.3;

// Multiplicative power factors. Visibility predates the rework; gust stability
// joins the chain because unstable wind is a viability problem, not a comfort
// problem.
const GUST_POWER = 0.45;
const VIS_POWER = 0.30;

// Additive quality composite weights. Wind quality and gust factor are NOT
// here: they act multiplicatively (gate and power factor). Thermal exposure,
// storm risk, and visibility are excluded by design (display-only /
// multiplicative / safety-flag), so these sum to 1.00 (within floating
// point) and a perfect day can reach 100 after rounding.
const QUALITY_WEIGHTS = {
  windConsistency: 0.22,
  seaState:        0.40,
  precipitation:   0.25,
  pressureTrend:   0.13,
} as const;

// Wind viability curves: knots -> 0-100, used as a multiplicative gate
// (windQuality / 100). Anchored to race-officer judgement: ~1 kt is no
// sailing (gate ~0.1), 6 kt is postponement territory (~0.27), 11 kt is
// pleasant club racing (~0.74), 13-16 kt is championship breeze (1.0).
const VIABILITY_CURVE_CLUB: [number, number][] = [
  [0,   0], [1,   9], [2,  13], [3,  16], [4,  20],
  [5,  24], [6,  27], [7,  38], [8,  52], [9,  62],
  [10, 68], [11, 74], [12, 85], [13, 96], [14, 100],
  [16, 100], [17, 95], [18, 88], [20, 78], [22, 62],
  [24, 46], [26, 33], [28, 22], [30, 13], [33, 6], [36, 3], [40, 0],
];

// HP optimum (P8-2, audit 10 F5 / exec 7). The 25-35 kt band HP riders travel for
// (Columbia Gorge, SF Bay) is peak experience, not a penalty: the earlier curve
// put its optimum at 18-20 kt and decayed hard above 24 kt (28 kt = 0.36), making
// 90+ mathematically impossible above 24 kt. The gate now holds a broad optimum
// across 18-30 kt and tapers honestly to the 40 kt storm-force zero (the documented
// storm-force exception, also hard-suppressed above 40 kt). The CLUB curve is
// untouched, and the gust >24 kt high-wind amplification is kept, so a gusty thermal
// day stays correctly moderate while a clean steady 28 kt day reads championship.
const VIABILITY_CURVE_HP: [number, number][] = [
  [0,   0], [2,   5], [4,   8], [6,  14], [8,  25],
  [10, 40], [12, 58], [13, 68], [14, 77], [15, 85],
  [16, 92], [17, 97], [18, 100], [19, 100], [20, 100],
  [22, 100], [24, 100], [26, 98], [28, 94], [30, 88],
  [32, 78], [34, 60], [36, 38], [38, 18], [40, 0],
];

const GUST_RATIO_CURVE: [number, number][] = [
  [1.0, 100], [1.2, 100], [1.3, 95], [1.4, 85], [1.5, 75],
  [1.6, 65], [1.7, 55], [1.8, 42], [1.9, 32], [2.0, 24],
  [2.2, 14], [2.5, 6], [3.0, 2],
];

const WIND_DIR_STD_CURVE: [number, number][] = [
  [0, 100], [10, 100], [20, 90], [30, 78], [40, 65],
  [50, 50], [60, 38], [75, 25], [90, 15], [120, 5],
];

const WAVE_HEIGHT_CURVE: [number, number][] = [
  [0.0, 95], [0.15, 98], [0.3, 100], [0.4, 92], [0.5, 82],
  [0.6, 72], [0.7, 60], [0.8, 50], [0.9, 40], [1.0, 32],
  [1.2, 22], [1.5, 12], [2.0, 4], [2.5, 0],
];

const THERMAL_CURVE: [number, number][] = [
  [-10, 0], [-5, 5], [0, 15], [3, 28], [5, 38],
  [7, 50], [9, 60], [10, 65], [12, 75], [14, 83],
  [16, 90], [18, 96], [20, 100], [22, 100], [24, 95],
  [26, 88], [28, 80], [30, 70], [32, 58], [35, 42],
  [38, 28], [42, 10],
];

const RAIN_CURVE: [number, number][] = [
  [0, 100], [0.2, 95], [0.5, 85], [1.0, 72], [2.0, 55],
  [3.0, 42], [5.0, 28], [8.0, 15], [12.0, 8], [20.0, 2],
];

const VISIBILITY_CURVE: [number, number][] = [
  [0, 0], [200, 5], [500, 15], [1000, 30], [2000, 50],
  [3000, 65], [5000, 80], [8000, 92], [10000, 100], [50000, 100],
];

// Steady pressure scores 100: a settled high is a perfect sailing forecast,
// and docking it points was what stopped perfect days reading above ~9.4.
const PRESSURE_TREND_CURVE: [number, number][] = [
  [-4.0, 0], [-3.0, 10], [-2.0, 25], [-1.5, 40], [-1.0, 55],
  [-0.5, 75], [-0.2, 95], [0.0, 100], [0.5, 100],
  [1.0, 100], [1.5, 92], [2.0, 80], [3.0, 60],
];

// ---------------------------------------------------------------------------
// Utilities
// ---------------------------------------------------------------------------

function lerp(curve: [number, number][], input: number): number {
  if (input <= curve[0][0]) return curve[0][1];
  if (input >= curve[curve.length - 1][0]) return curve[curve.length - 1][1];
  for (let i = 1; i < curve.length; i++) {
    const [x0, y0] = curve[i - 1];
    const [x1, y1] = curve[i];
    if (input <= x1) {
      const t = (input - x0) / (x1 - x0);
      return y0 + t * (y1 - y0);
    }
  }
  return curve[curve.length - 1][1];
}

function clamp(v: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, v));
}

function toKnots(kmh: number): number {
  return kmh * KMH_TO_KNOTS;
}

function circularMean(degrees: number[]): number {
  if (degrees.length === 0) return 0;
  const toRad = Math.PI / 180;
  let sinSum = 0;
  let cosSum = 0;
  for (const d of degrees) {
    sinSum += Math.sin(d * toRad);
    cosSum += Math.cos(d * toRad);
  }
  const m = Math.atan2(sinSum / degrees.length, cosSum / degrees.length);
  return ((m * 180) / Math.PI + 360) % 360;
}

function circularStdDev(degrees: number[]): number {
  if (degrees.length < 2) return 0;
  const n = degrees.length;
  const toRad = Math.PI / 180;
  let sinSum = 0;
  let cosSum = 0;
  for (const d of degrees) {
    sinSum += Math.sin(d * toRad);
    cosSum += Math.cos(d * toRad);
  }
  const R = Math.sqrt((sinSum / n) ** 2 + (cosSum / n) ** 2);
  if (R >= 1) return 0;
  if (R <= 0.001) return 180;
  return Math.sqrt(-2 * Math.log(R)) * (180 / Math.PI);
}

function mean(arr: number[]): number {
  if (arr.length === 0) return 0;
  return arr.reduce((a, b) => a + b, 0) / arr.length;
}

function angularDiff(a: number, b: number): number {
  let diff = Math.abs(a - b) % 360;
  if (diff > 180) diff = 360 - diff;
  return diff;
}

function isWindInExposedSector(
  windDir: number,
  sectors: [number, number][],
): boolean {
  for (const [start, end] of sectors) {
    if (start <= end) {
      if (windDir >= start && windDir <= end) return true;
    } else {
      if (windDir >= start || windDir <= end) return true;
    }
  }
  return false;
}

function hasThunderstorm(codes: number[]): boolean {
  return codes.some(c => c >= 95 && c <= 99);
}

function hasFog(codes: number[]): boolean {
  return codes.some(c => c === 45 || c === 48);
}

// ---------------------------------------------------------------------------
// Window aggregation
// ---------------------------------------------------------------------------

interface WindowStats {
  avgWindKts: number;
  maxWindKts: number;
  minWindKts: number;
  avgGustKts: number;
  maxGustKts: number;
  windDirs: number[];
  avgGustRatio: number;
  maxGustRatio: number;
  safetyMaxGustRatio: number | null;
  avgShearRatio: number | null;
  avgWindWaveHeight: number | null;
  maxWindWaveHeight: number | null;
  avgWindWavePeriod: number | null;
  avgWaveHeight: number | null;
  avgSwellHeight: number | null;
  avgSwellPeriod: number | null;
  avgCurrentVelocity: number | null;
  avgCurrentDir: number | null;
  avgWindDir: number;
  avgApparentTemp: number;
  minApparentTemp: number;
  avgRainMmHr: number;
  maxRainMmHr: number;
  totalRainMm: number;
  weatherCodes: number[];
  avgVisibility: number;
  minVisibility: number;
  maxCape: number;
  minLiftedIndex: number;
  pressureChangePerHr: number;
  pressures: number[];
  startHour: number;
  endHour: number;
}

function aggregateWindow(hours: HourlyDatum[]): WindowStats {
  const windSpeedsKts = hours.map(h => toKnots(h.wind_speed_10m_kmh));
  const gustSpeedsKts = hours.map(h => toKnots(h.wind_gust_10m_kmh));
  const windDirs = hours.map(h => h.wind_dir_10m_deg);

  const gustRatios = hours.map(h => {
    const sustained = h.wind_speed_10m_kmh;
    const gust = h.wind_gust_10m_kmh;
    if (sustained < 2) return 1.0;
    return gust / sustained;
  });
  const safetyGustRatios = hours.flatMap(h => {
    if (toKnots(h.wind_speed_10m_kmh) < 3) return [];
    return [h.wind_gust_10m_kmh / h.wind_speed_10m_kmh];
  });

  const shearRatios: number[] = [];
  for (const h of hours) {
    if (h.wind_speed_80m_kmh != null && h.wind_speed_10m_kmh > 3) {
      shearRatios.push(h.wind_speed_80m_kmh / h.wind_speed_10m_kmh);
    }
  }

  const windWaveHeights = hours.map(h => h.wind_wave_height_m).filter((v): v is number => v != null);
  const windWavePeriods = hours.map(h => h.wind_wave_period_s).filter((v): v is number => v != null);
  const waveHeights = hours.map(h => h.wave_height_m).filter((v): v is number => v != null);
  const swellHeights = hours.map(h => h.swell_height_m).filter((v): v is number => v != null);
  const swellPeriods = hours.map(h => h.swell_period_s).filter((v): v is number => v != null);
  const currentVelocities = hours.map(h => h.ocean_current_velocity_ms).filter((v): v is number => v != null);
  const currentDirs = hours.map(h => h.ocean_current_dir_deg).filter((v): v is number => v != null);

  const pressures = hours.map(h => h.pressure_msl_hpa);
  let pressureChangePerHr = 0;
  if (pressures.length >= 2) {
    pressureChangePerHr = (pressures[pressures.length - 1] - pressures[0]) / (pressures.length - 1);
  }

  return {
    avgWindKts: mean(windSpeedsKts),
    maxWindKts: Math.max(...windSpeedsKts),
    minWindKts: Math.min(...windSpeedsKts),
    avgGustKts: mean(gustSpeedsKts),
    maxGustKts: Math.max(...gustSpeedsKts),
    windDirs,
    avgGustRatio: mean(gustRatios),
    maxGustRatio: Math.max(...gustRatios),
    safetyMaxGustRatio: safetyGustRatios.length > 0
      ? Math.max(...safetyGustRatios)
      : null,
    avgShearRatio: shearRatios.length > 0 ? mean(shearRatios) : null,
    avgWindWaveHeight: windWaveHeights.length > 0 ? mean(windWaveHeights) : null,
    maxWindWaveHeight: windWaveHeights.length > 0 ? Math.max(...windWaveHeights) : null,
    avgWindWavePeriod: windWavePeriods.length > 0 ? mean(windWavePeriods) : null,
    avgWaveHeight: waveHeights.length > 0 ? mean(waveHeights) : null,
    avgSwellHeight: swellHeights.length > 0 ? mean(swellHeights) : null,
    avgSwellPeriod: swellPeriods.length > 0 ? mean(swellPeriods) : null,
    avgCurrentVelocity: currentVelocities.length > 0 ? mean(currentVelocities) : null,
    avgCurrentDir: currentDirs.length > 0 ? circularMean(currentDirs) : null,
    avgWindDir: circularMean(windDirs),
    avgApparentTemp: mean(hours.map(h => h.apparent_temp_c)),
    minApparentTemp: Math.min(...hours.map(h => h.apparent_temp_c)),
    avgRainMmHr: mean(hours.map(h => h.rain_mm)),
    maxRainMmHr: Math.max(...hours.map(h => h.rain_mm)),
    totalRainMm: hours.reduce((s, h) => s + h.rain_mm, 0),
    weatherCodes: hours.map(h => h.weather_code),
    avgVisibility: mean(hours.map(h => h.visibility_m)),
    minVisibility: Math.min(...hours.map(h => h.visibility_m)),
    maxCape: Math.max(...hours.map(h => h.cape_jkg)),
    minLiftedIndex: Math.min(...hours.map(h => h.lifted_index)),
    pressureChangePerHr,
    pressures,
    startHour: hours[0].hour,
    endHour: hours[hours.length - 1].hour,
  };
}

// ---------------------------------------------------------------------------
// Sub-factor scoring
// ---------------------------------------------------------------------------

function scoreWindQuality(stats: WindowStats, category: BoatCategory): number {
  const curve = category === 'club' ? VIABILITY_CURVE_CLUB : VIABILITY_CURVE_HP;
  const baseScore = lerp(curve, stats.avgWindKts);

  const range = stats.maxWindKts - stats.minWindKts;
  const variabilityRatio = stats.avgWindKts > 2 ? range / stats.avgWindKts : 0;

  let variabilityPenalty = 0;
  if (variabilityRatio > 0.3) {
    variabilityPenalty = clamp((variabilityRatio - 0.3) * 30, 0, 20);
  }

  let shearPenalty = 0;
  if (stats.avgShearRatio != null && stats.avgShearRatio > 1.8) {
    shearPenalty = clamp((stats.avgShearRatio - 1.8) * 15, 0, 15);
  }

  return clamp(Math.round(baseScore - variabilityPenalty - shearPenalty), 0, 100);
}

function scoreGustFactor(
  stats: WindowStats,
  category: BoatCategory,
  exposedSectors?: [number, number][],
): number {
  // Below 3 kt the gust ratio is statistical noise. Return neutral (100 ->
  // multiplier 1.0, no effect): the wind viability gate owns becalmed days.
  // The old additive model returned 80 here, actively rewarding calm.
  // This is a deliberate step: a 3.5 kt window with a wild gust ratio can
  // score below a 2.9 kt one, but both sit deep in the Becalmed band where
  // the gate dominates, so smoothness is not worth the complexity.
  if (stats.avgWindKts < 3) return 100;

  const baseScore = lerp(GUST_RATIO_CURVE, stats.avgGustRatio);

  const highWindThreshold = category === 'club' ? 18 : 24;
  let amplifiedScore = baseScore;

  if (stats.avgWindKts > highWindThreshold) {
    const penalty = 100 - baseScore;
    const amplification = 1.0 + clamp((stats.avgWindKts - highWindThreshold) / 10, 0, 0.5);
    amplifiedScore = clamp(100 - penalty * amplification, 0, 100);
  }

  let spikePenalty = 0;
  if (stats.maxGustRatio > 2.0 && stats.maxGustRatio > stats.avgGustRatio + 0.3) {
    spikePenalty = clamp((stats.maxGustRatio - 2.0) * 12, 0, 15);
  }

  let offshorePenalty = 0;
  if (exposedSectors && exposedSectors.length > 0) {
    const isOnshore = isWindInExposedSector(stats.avgWindDir, exposedSectors);
    if (!isOnshore) {
      offshorePenalty = 6;
    }
  }

  return clamp(Math.round(amplifiedScore - spikePenalty - offshorePenalty), 0, 100);
}

function scoreWindConsistency(
  stats: WindowStats,
  exposedSectors?: [number, number][],
): number {
  const stdDev = circularStdDev(stats.windDirs);
  let baseScore = lerp(WIND_DIR_STD_CURVE, stdDev);

  if (exposedSectors && exposedSectors.length > 0) {
    const isOnshore = isWindInExposedSector(stats.avgWindDir, exposedSectors);
    if (!isOnshore) {
      const lightWindAmplifier = clamp((15 - stats.avgWindKts) / 10, 0.3, 1.0);
      baseScore -= Math.round(10 * lightWindAmplifier);
    }
  }

  return clamp(Math.round(baseScore), 0, 100);
}

function scoreSeaState(
  stats: WindowStats,
  venueType: VenueType,
  exposedSectors?: [number, number][],
): number {
  if (venueType === 'inland') return 95;

  let waveHeight: number;
  if (stats.avgWindWaveHeight != null) {
    waveHeight = stats.avgWindWaveHeight;
  } else if (stats.avgWaveHeight != null) {
    waveHeight = stats.avgWaveHeight;
  } else {
    return 85;
  }

  let baseScore = lerp(WAVE_HEIGHT_CURVE, waveHeight);

  // Steep swell penalty
  if (
    stats.avgSwellHeight != null &&
    stats.avgSwellPeriod != null &&
    stats.avgSwellHeight > 0.8
  ) {
    if (stats.avgSwellPeriod < 5) {
      baseScore -= 20;
    } else if (stats.avgSwellPeriod < 7) {
      const steepnessPenalty = clamp(
        (stats.avgSwellHeight - 0.8) * 15 * ((7 - stats.avgSwellPeriod) / 2),
        0, 18
      );
      baseScore -= steepnessPenalty;
    }
  }

  // Wind against tide
  if (
    stats.avgCurrentVelocity != null &&
    stats.avgCurrentDir != null &&
    stats.avgCurrentVelocity > 0.3
  ) {
    const windTideAngle = angularDiff(stats.avgWindDir, stats.avgCurrentDir);

    if (windTideAngle > 120) {
      const oppositionStrength = (windTideAngle - 120) / 60;
      const currentFactor = clamp(stats.avgCurrentVelocity / 1.5, 0, 1);
      const windFactor = clamp(stats.avgWindKts / 25, 0.3, 1);
      const windTidePenalty = Math.round(oppositionStrength * currentFactor * windFactor * 25);
      baseScore -= windTidePenalty;
    } else if (windTideAngle < 60) {
      const alignmentBonus = clamp(
        stats.avgCurrentVelocity * 3 * ((60 - windTideAngle) / 60),
        0, 8
      );
      baseScore += alignmentBonus;
    }
  }

  // Fetch / shore bearing
  if (exposedSectors && exposedSectors.length > 0) {
    const isOnshore = isWindInExposedSector(stats.avgWindDir, exposedSectors);
    if (!isOnshore && waveHeight > 0.3) {
      const fetchBonus = clamp(Math.round((100 - baseScore) * 0.3), 0, 18);
      baseScore += fetchBonus;
    }
  }

  if (venueType === 'estuary' && waveHeight < 1.0) {
    baseScore *= 1.08;
  }

  return clamp(Math.round(baseScore), 0, 100);
}

function scoreThermalExposure(stats: WindowStats): number {
  let baseScore = lerp(THERMAL_CURVE, stats.avgApparentTemp);

  if (stats.avgRainMmHr > 1.5 && stats.avgApparentTemp < 12) {
    const rainColdPenalty = clamp(
      (12 - stats.avgApparentTemp) * (stats.avgRainMmHr / 3) * 2,
      0, 15
    );
    baseScore -= rainColdPenalty;
  }

  if (stats.avgWindKts > 18 && stats.avgApparentTemp < 10) {
    const sprayColdPenalty = clamp(
      ((stats.avgWindKts - 18) / 5) * ((10 - stats.avgApparentTemp) / 5) * 8,
      0, 12
    );
    baseScore -= sprayColdPenalty;
  }

  if (stats.minApparentTemp < stats.avgApparentTemp - 3) {
    const dropPenalty = clamp(
      (stats.avgApparentTemp - stats.minApparentTemp - 3) * 2,
      0, 8
    );
    baseScore -= dropPenalty;
  }

  return clamp(Math.round(baseScore), 0, 100);
}

function scorePrecipitation(stats: WindowStats): number {
  // Storm codes deliberately do not cap this: lightning is the safety
  // layer's call (computeSailingSafety), and storm rain already shows up
  // here in mm/hr.
  return clamp(Math.round(lerp(RAIN_CURVE, stats.avgRainMmHr)), 0, 100);
}

function scoreVisibility(stats: WindowStats): number {
  let baseScore = lerp(VISIBILITY_CURVE, stats.minVisibility);

  if (hasFog(stats.weatherCodes)) {
    baseScore = Math.min(baseScore, 25);
  }

  if (stats.avgVisibility < 5000 && baseScore > 60) {
    const hazePenalty = clamp((5000 - stats.avgVisibility) / 500, 0, 10);
    baseScore -= hazePenalty;
  }

  return clamp(Math.round(baseScore), 0, 100);
}

// Display-only since the storm-policy unification: shown in the sub-score
// panel but carries zero weight in the composite. The safety flags carry
// the actionable warning.
function scoreStormRisk(stats: WindowStats): number {
  if (hasThunderstorm(stats.weatherCodes)) return 0;
  if (stats.maxCape > 1500 && stats.minLiftedIndex < -3) return 12;
  if (stats.maxCape > 1000 && stats.minLiftedIndex < -2) return 30;
  if (stats.maxCape > 700 && stats.minLiftedIndex < -1) return 50;
  if (stats.maxCape > 500) return 65;
  if (stats.maxCape > 300) return 78;
  if (stats.maxCape > 100) return 90;
  return 100;
}

function scorePressureTrend(stats: WindowStats): number {
  return clamp(
    Math.round(lerp(PRESSURE_TREND_CURVE, stats.pressureChangePerHr)),
    0, 100
  );
}

// ---------------------------------------------------------------------------
// Suppression
// ---------------------------------------------------------------------------

interface SuppressionResult {
  suppressed: boolean;
  reason: string | null;
}

function checkSuppression(_stats: WindowStats): SuppressionResult {
  // Wind, gusts and visibility have continuous physical quality responses.
  // Warnings are independent; no hazard-only threshold overwrites the score.
  return { suppressed: false, reason: null };
}

// ---------------------------------------------------------------------------
// Safety indicators - separate from score (storm-policy unification)
// ---------------------------------------------------------------------------

function computeSailingSafety(
  stats: WindowStats,
  category: BoatCategory,
): SailingSafetyIndicator[] {
  const indicators: SailingSafetyIndicator[] = [];

  if (hasThunderstorm(stats.weatherCodes)) {
    indicators.push({
      level: 'red',
      label: 'Thunderstorm',
      detail: 'Thunderstorms during racing hours. A mast is a lightning conductor; racing will be abandoned.',
    });
  } else if (stats.maxCape > 1000 && stats.minLiftedIndex < -2) {
    indicators.push({
      level: 'amber',
      label: 'Storm risk',
      detail: `Unstable atmosphere (CAPE ${Math.round(stats.maxCape)} J/kg). Watch for building cells and squalls.`,
    });
  }

  if (stats.maxGustKts > 50 || stats.maxWindKts > 40) {
    indicators.push({
      level: 'red',
      label: 'Extreme wind',
      detail: `${Math.round(stats.maxWindKts)} kt sustained, gusts ${Math.round(stats.maxGustKts)} kt. Beyond dinghy limits.`,
    });
  }

  if (stats.minVisibility < 1000) {
    indicators.push({
      level: stats.minVisibility < 500 ? 'red' : 'amber',
      label: 'Poor visibility',
      detail: 'Visibility below safe race-management limits. Check with the club before launching.',
    });
  }

  if (stats.minApparentTemp < -5 && stats.totalRainMm > 0.5) {
    indicators.push({
      level: 'amber',
      label: 'Cold exposure',
      detail: 'Cold rain or sleet during racing hours. Use drysuit-level kit and check club safety cover.',
    });
  }

  if (stats.safetyMaxGustRatio !== null && stats.safetyMaxGustRatio >= 2) {
    const categoryLabel = category === 'club' ? 'club dinghy' : 'high-performance';
    indicators.push({
      level: 'amber',
      label: 'Severe gust volatility',
      detail: `Gusts reach ${stats.safetyMaxGustRatio.toFixed(1)} times the sustained wind for ${categoryLabel} conditions. Expect abrupt load changes and allow extra margin when planning the session.`,
      boatCategory: category,
    });
  }

  return indicators;
}

// ---------------------------------------------------------------------------
// Label assignment - shared platform bands on the 0-100 scale (x10 of the
// 0-10 thresholds every other sport uses), with sailing vocabulary at the
// bottom.
// ---------------------------------------------------------------------------

function assignLabel(score: number): SailingLabel {
  if (score >= 90) return 'Epic';
  if (score >= 75) return 'Firing';
  if (score >= 65) return 'Great';
  if (score >= 50) return 'Good';
  if (score >= 35) return 'Fair';
  if (score >= 20) return 'Poor';
  return 'Becalmed';
}

export function sailingLabel(score: number): SailingLabel {
  return assignLabel(score);
}

// ---------------------------------------------------------------------------
// Main scoring function
// ---------------------------------------------------------------------------

export function scoreSailing(
  hourlyData: HourlyDatum[],
  category: BoatCategory = 'club',
  venueType: VenueType = 'inland',
  exposedSectors?: [number, number][],
): SailingScore {
  const daylightHours = uniqueOrderedHours(hourlyData).filter(h => h.is_day === 1);
  const racingHours = daylightHours.filter(
    h => h.hour >= RACING_HOURS_START && h.hour <= RACING_HOURS_END,
  );
  // High-latitude winter fallback: when daylight barely overlaps racing
  // hours, score whatever daylight exists rather than nothing.
  const poolHours = racingHours.length >= WINDOW_SIZE ? racingHours : daylightHours;

  // A genuine two- or three-hour winter session is useful. Shortening is
  // allowed only when the available daylight itself is short, never to bridge gaps.
  const windowSize = Math.min(WINDOW_SIZE, poolHours.length);
  if (windowSize < 2) {
    return {
      score: 0,
      label: 'Insufficient data',
      boatCategory: category,
      venueType,
      bestWindow: { startHour: 0, endHour: 0 },
      subScores: {
        windQuality: 0, gustFactor: 0, windConsistency: 0, seaState: 0,
        thermalExposure: 0, precipitation: 0, visibility: 0, stormRisk: 0, pressureTrend: 0,
      },
      scoreFactors: EMPTY_SCORE_FACTORS,
      qualityContributions: EMPTY_QUALITY_CONTRIBUTIONS,
      suppressed: true,
      suppressionReason: 'Insufficient daylight hours for a sailing window',
      safety: poolHours.length > 0
        ? computeSailingSafety(aggregateWindow(poolHours), category)
        : [],
    };
  }

  const sorted = [...poolHours].sort((a, b) => a.hour - b.hour);
  // Safety scans the whole sailing day, not just the best window: a storm
  // cell either side of your race slot is still a storm at the venue.
  const daySafety = computeSailingSafety(aggregateWindow(sorted), category);

  let bestScore = -1;
  let bestResult: {
    composite: number;
    subScores: SubScores;
    startHour: number;
    endHour: number;
    suppression: SuppressionResult;
    scoreFactors: SailingScore['scoreFactors'];
    qualityContributions: SailingScore['qualityContributions'];
  } | null = null;
  const allComposites: number[] = [];

  for (const windowHours of consecutiveHourlyWindows(sorted, windowSize)) {
    const stats = aggregateWindow(windowHours);
    const suppression = checkSuppression(stats);

    const subScores: SubScores = {
      windQuality:     scoreWindQuality(stats, category),
      gustFactor:      scoreGustFactor(stats, category, exposedSectors),
      windConsistency: scoreWindConsistency(stats, exposedSectors),
      seaState:        scoreSeaState(stats, venueType, exposedSectors),
      thermalExposure: scoreThermalExposure(stats),
      precipitation:   scorePrecipitation(stats),
      visibility:      scoreVisibility(stats),
      stormRisk:       scoreStormRisk(stats),
      pressureTrend:   scorePressureTrend(stats),
    };

    // Multiplicative chain: wind viability gates everything. No wind means
    // no score, regardless of how pleasant the sunshine and flat water are.
    const windViability = subScores.windQuality / 100;
    const gustStability = Math.pow(
      Math.max(0.01, subScores.gustFactor / 100),
      GUST_POWER,
    );
    const normalVisibility = Math.pow(Math.max(0.01, subScores.visibility / 100), VIS_POWER);
    // Losing the marks and wave references is a physical quality cost. Blend
    // into it continuously below 2 km instead of switching the session off.
    const sighting = clamp(stats.minVisibility / 2000, 0, 1);
    const visF = normalVisibility * (sighting * sighting * (3 - 2 * sighting));

    const qualityContributions = Object.freeze({
      windConsistency: subScores.windConsistency * QUALITY_WEIGHTS.windConsistency,
      seaState: subScores.seaState * QUALITY_WEIGHTS.seaState,
      precipitation: subScores.precipitation * QUALITY_WEIGHTS.precipitation,
      pressureTrend: subScores.pressureTrend * QUALITY_WEIGHTS.pressureTrend,
    });
    const quality = Object.values(qualityContributions).reduce((sum, contribution) => sum + contribution, 0);
    const scoreFactors = Object.freeze({
      windViability,
      gustStability,
      qualityPoints: quality,
      visibilityMultiplier: visF,
    });

    let composite = Math.round(
      clamp(
        composeScore(SAILING_WINDOW_CHAIN, {
          windViability,
          gustStability,
          quality,
          visibility: visF,
        }).compressed,
        0, 100,
      ),
    );

    if (suppression.suppressed) {
      composite = 0;
    }

    allComposites.push(composite);

    if (composite > bestScore) {
      bestScore = composite;
      bestResult = {
        composite,
        subScores,
        startHour: windowHours[0].hour,
        endHour: windowHours[windowHours.length - 1].hour + 1,
        suppression,
        scoreFactors,
        qualityContributions,
      };
    }
  }

  if (!bestResult) {
    return {
      score: 0,
      label: 'Insufficient data',
      boatCategory: category,
      venueType,
      bestWindow: { startHour: 0, endHour: 0 },
      subScores: {
        windQuality: 0, gustFactor: 0, windConsistency: 0, seaState: 0,
        thermalExposure: 0, precipitation: 0, visibility: 0, stormRisk: 0, pressureTrend: 0,
      },
      scoreFactors: EMPTY_SCORE_FACTORS,
      qualityContributions: EMPTY_QUALITY_CONTRIBUTIONS,
      suppressed: true,
      suppressionReason: 'No valid scoring window found',
      safety: daySafety,
    };
  }

  // Window-heavy blend across racing hours: sailors sail sessions, not whole
  // days. The best 4-hour window dominates so a classic sea-breeze afternoon
  // is not punished for a glassy morning; the pool average rewards days that
  // are good throughout.
  const poolAvg = allComposites.reduce((a, b) => a + b, 0) / allComposites.length;
  const blended = Math.round(clamp(
    bestResult.composite * BEST_WINDOW_WEIGHT + poolAvg * POOL_AVG_WEIGHT,
    0, 100,
  ));

  return {
    score: blended,
    label: assignLabel(blended),
    boatCategory: category,
    venueType,
    bestWindow: { startHour: bestResult.startHour, endHour: bestResult.endHour },
    subScores: bestResult.subScores,
    scoreFactors: bestResult.scoreFactors,
    qualityContributions: bestResult.qualityContributions,
    suppressed: bestResult.suppression.suppressed,
    suppressionReason: bestResult.suppression.reason,
    safety: daySafety,
  };
}

// ---------------------------------------------------------------------------
// Dual scoring convenience
// ---------------------------------------------------------------------------

export interface DualSailingScore {
  club: SailingScore;
  highPerformance: SailingScore;
}

export function scoreSailingDual(
  hourlyData: HourlyDatum[],
  venueType: VenueType = 'inland',
  exposedSectors?: [number, number][],
): DualSailingScore {
  return {
    club: scoreSailing(hourlyData, 'club', venueType, exposedSectors),
    highPerformance: scoreSailing(hourlyData, 'high_performance', venueType, exposedSectors),
  };
}

// =============================================================================
// Revision 2 (all-sports engine design 6.2 and 6.3, work package WP-S5)
// =============================================================================
//
// Everything above is revision 1, today's stored truth, and never changes:
// engines/core/__tests__/leaf-revision-one.test.ts pins its outputs. Revision 2
// is reached only through scoreSailingRevisionTwo with an explicit revision 2
// `formula` (engines/core/formula.ts convention), which only the revision 2
// kernels in engines/sailing/r2/ pass, and through the per-hour safety rows
// below, which only the v2 safety modules in engines/sailing/safety/ and the
// revision 2 window choice read. Findings addressed:
//
//   SAIL-1  Wind against tide compares the current's heading (Open-Meteo reports
//           where the current flows towards) with the wind's heading,
//           (windFrom + 180) mod 360, using the window's vector-mean current, so a
//           window spanning a tidal turn sees the flood and ebb cancel.
//   SAIL-3  Strong-wind ladder per boat category (sailingHourSafetyRows), and an
//           overpowered day reads "Too windy", never "Becalmed": a best window above
//           the optimum whose wind gate has removed half the score, or, below the
//           Fair band, one whose mean wind is above the optimum and at or above the
//           ladder's amber rung (owner decision O-11, WP-S5 option b).
//   SAIL-4  The high-performance viability curve tapers from 22 kt to about 0.5
//           by 27-28 kt (the SAIL-3 flags ship first, as stage S2 before S9).
//           Withdrawn on 1 October 2026 and restored the same day, before any
//           shadow registration (docs/handover/phase-2c.md, "Accelerated rollout,
//           Phase A"): the ladder never lowers a score (a red day keeps its number
//           and reads "No safe window"), so without the taper a steady 28 kt day
//           scores about 92; the owner decides the Columbia Gorge and SF Bay
//           consequence (D15) on H1's prime-season evidence.
//   SAIL-6  The best window comes from windows without a red hour; when every
//           window is red the day keeps its number and reads "No safe window"
//           (review decision 14), whatever made them red.
//   SAIL-7  Missing inputs never become confident values. A missing 10 m wind
//           makes the hour unavailable (it never enters a window); a missing
//           modifier takes its neutral value: gust equal to the sustained wind,
//           no precipitation, 10 km visibility, no current, a 10 s swell period,
//           no thunderstorm code for the window choice, the direction-averaged
//           factor for a missing wind direction, and an additive quality term
//           with no data (sea state, wind consistency) drops out with the other
//           weights renormalised. "Insufficient data" is a null score, never 0.
//   SAIL-12 Gust volatility is flagged only once the sustained wind is usable.
//           Every safety row reads a missing input that could hide its hazard as
//           unavailable, never clear (design 2.5 and 2.7): a missing weather code
//           ("Storm forecast unavailable", even beside proven storm risk, owner
//           decision O-11), CAPE and lifted index that cannot rule out storm risk,
//           a missing sustained wind beside a strong gust, a missing visibility.
//           Cold rain is a modifier (O-11): a missing apparent temperature or
//           precipitation is neutral and adds no row.
//   SAIL-13 The pressure trend carries weight 0 (reported as hPa per hour for
//           display, provisional until validated against outcomes); the other
//           quality weights keep their proportions and sum to 1.
//   SAIL-15 Visibility applies once: the stronger of the haze curve and the
//           mark-sighting blend, never their product, and no weather-code fog cap
//           on top of the measured visibility.
//
// Windows are exactly 4 consecutive available daylight hours inside revision 1's
// racing hours, the 09:00 to 18:00 hour slots (RACING_HOURS_START to
// RACING_HOURS_END inclusive), so the last window is 15:00 to 19:00: the missing-
// data day rule of design 2.5 (engines/core/missing-data.ts
// DAY_WINDOW_RULES.sailing). Revision 2 first ended every window by 18:00, which
// no review finding asked for and which dropped the late sea-breeze window at
// thermal venues (SF Bay, Vaal Dam, Biscayne Bay on captured production days);
// it was restored on 1 October 2026, before the first shadow registration
// (docs/handover/phase-2c.md). The 80 m shear
// penalty of revision 1 is not part of revision 2: the day engine's input
// vocabulary has no 80 m wind, and role tables must list every input a kernel
// reads (docs/handover/WP-S5.md).

/**
 * The formula argument of the revision 2 entry points: the revision only, no options (the
 * base of engines/core/formula.ts LeafFormula; its default option type cannot hold a revision,
 * see docs/handover/WP-S5.md).
 */
export type SailingFormula = Readonly<{ revision: number }>;

export const SAILING_REVISION_TWO_FORMULA: Readonly<{ revision: 2 }> = Object.freeze({ revision: 2 });

/**
 * One local hour for revision 2, presence already resolved by the caller (the kernel
 * reads presence with engines/core/missing-data.ts inputPresent): null means the
 * input is missing, never a zero fill.
 */
export type SailingEngineHour = Readonly<{
  hour: number;
  isDay: boolean;
  /** The driver: null makes the hour unavailable. */
  windKmh: number | null;
  windFromDeg: number | null;
  gustKmh: number | null;
  precipMm: number | null;
  visibilityM: number | null;
  weatherCode: number | null;
  pressureHpa: number | null;
  windWaveHeightM: number | null;
  waveHeightM: number | null;
  swellHeightM: number | null;
  swellPeriodS: number | null;
  currentMs: number | null;
  /** The direction the current flows towards (Open-Meteo convention). */
  currentTowardsDeg: number | null;
}>;

/** One local hour for the v2 safety rows; null means missing. */
export type SailingSafetyHour = Readonly<{
  hour: number;
  windKmh: number | null;
  gustKmh: number | null;
  weatherCode: number | null;
  capeJkg: number | null;
  liftedIndex: number | null;
  visibilityM: number | null;
  /**
   * The hour's relative humidity in % (model-fog.ts), the visibility row's saturation test
   * (accelerated rollout Phase E). Null or omitted when unknown: the air counts as saturated, so a
   * missing humidity never lowers the row.
   */
  relativeHumidityPct?: number | null;
  apparentTempC: number | null;
  precipMm: number | null;
}>;

/** Strong-wind ladder per boat category (SAIL-3; owner decision 10). Knots, inclusive. */
export const SAILING_STRONG_WIND_LADDER = Object.freeze({
  club: Object.freeze({ amberMeanKts: 20, amberGustKts: 25, redMeanKts: 25, redGustKts: 30 }),
  high_performance: Object.freeze({ amberMeanKts: 22, amberGustKts: 27, redMeanKts: 27, redGustKts: 32 }),
});

/** Sustained wind below which a gust ratio is drifting-air noise, not volatility (SAIL-12). */
export const SAILING_USABLE_WIND_KTS = 6;

const SAILING_SAFETY_THRESHOLDS = Object.freeze({
  gustVolatilityRatio: 2,
  stormRiskMinCapeJkg: 1000,
  stormRiskMaxLiftedIndex: -2,
  visibilityAmberBelowM: 1000,
  visibilityRedBelowM: 500,
  coldExposureApparentBelowC: -5,
  coldExposureMinPrecipMm: 0.1,
});

/** Revision 2 high-performance viability: flat to 22 kt, about 0.5 by 27-28 kt (SAIL-4). */
const VIABILITY_CURVE_HP_R2: [number, number][] = [
  [0,   0], [2,   5], [4,   8], [6,  14], [8,  25],
  [10, 40], [12, 58], [13, 68], [14, 77], [15, 85],
  [16, 92], [17, 97], [18, 100], [20, 100], [22, 100],
  [23, 96], [24, 88], [25, 78], [26, 67], [27, 56],
  [28, 46], [30, 32], [32, 22], [34, 14], [36, 8], [38, 3], [40, 0],
];

/**
 * Direction-averaging step for a missing wind direction. Equal to
 * engines/core/missing-data.ts DIRECTION_AVERAGE_STEP_DEG (a test pins it); this
 * leaf may import only the engine declaration modules.
 */
const R2_DIRECTION_STEP_DEG = 15;

/** Every revision 2 constant, plain JSON: the kernels hash it into their formula fingerprint. */
export const SAILING_REVISION_TWO_PARAMS = Object.freeze({
  /** Window hours are fromHour to toHour - 1 (toHour exclusive): revision 1's racing hours 09:00 to 18:00. */
  window: Object.freeze({ hours: 4, fromHour: RACING_HOURS_START, toHour: RACING_HOURS_END + 1, daylightOnly: true }),
  blend: Object.freeze({ bestWindow: 0.7, poolAverage: 0.3 }),
  viability: Object.freeze({ club: VIABILITY_CURVE_CLUB, high_performance: VIABILITY_CURVE_HP_R2 }),
  /** The flat top of each viability curve, knots. */
  optimumKnots: Object.freeze({ club: Object.freeze([14, 16] as const), high_performance: Object.freeze([18, 22] as const) }),
  qualityWeights: Object.freeze({ windConsistency: 0.22, seaState: 0.40, precipitation: 0.25, pressureTrend: 0 }),
  gustPower: 0.45,
  visibility: Object.freeze({ power: 0.30, sightingFullM: 2000, rule: 'min(haze curve ^ power, sighting blend)' }),
  inlandSeaState: 95,
  neutral: Object.freeze({
    gust: 'sustained',
    precipMm: 0,
    visibilityM: 10000,
    weatherCode: 0,
    currentMs: 0,
    swellPeriodS: 10,
    steepSwellHeightM: 0,
    windDirection: 'direction-averaged',
    missingQualityTerm: 'dropped, weights renormalised',
    directionStepDeg: R2_DIRECTION_STEP_DEG,
  }),
  windAgainstTide: Object.freeze({
    convention: 'current towards versus wind towards (windFrom + 180)',
    currentMean: 'vector',
    minCurrentMs: 0.3,
    opposedAboveDeg: 120,
    alignedBelowDeg: 60,
    maxPenalty: 25,
    maxBonus: 8,
  }),
  labels: Object.freeze({
    overpoweredBelowViability: 50,
    underpoweredBelowViability: 50,
    /**
     * Below this score (the bands that would read "Poor"), a best window whose mean wind is above
     * the optimum and at or above the ladder's amber mean reads "Too windy" (O-11, WP-S5 option b).
     */
    overpoweredBottomBandsBelowScore: 35,
  }),
  ladder: SAILING_STRONG_WIND_LADDER,
  usableWindKts: SAILING_USABLE_WIND_KTS,
  safety: SAILING_SAFETY_THRESHOLDS,
});

export type SailingRevisionTwoLabel =
  | 'Epic' | 'Firing' | 'Great' | 'Good' | 'Fair' | 'Poor' | 'Becalmed' | 'Too windy' | 'No safe window';
export type SailingRevisionTwoLabelKind = 'quality' | 'overpowered' | 'no-safe-window';

export type SailingRevisionTwoWindow = Readonly<{
  startHour: number;
  /** Exclusive. */
  endHour: number;
  composite: number;
  /** Any hour of the window carries a red safety row (SAIL-6). */
  red: boolean;
  meanWindKts: number;
  maxGustKts: number;
  windQuality: number;
  gustFactor: number;
  /** Null when fewer than two hours hold a wind direction (the term drops out). */
  windConsistency: number | null;
  /** Null at a coastal or estuary venue with no wave height in the window (the term drops out). */
  seaState: number | null;
  precipitation: number;
  visibility: number;
  windViability: number;
  gustStability: number;
  qualityPoints: number;
  visibilityFactor: number;
  /** Provisional, weight 0 (SAIL-13): hPa per hour over the window, null with fewer than two readings. */
  pressureTrendHpaPerHour: number | null;
}>;

export type SailingRevisionTwoDay = Readonly<{
  /** 0-100 at native precision; null when no complete window exists (never 0 "Becalmed"). */
  score: number | null;
  label: SailingRevisionTwoLabel | null;
  labelKind: SailingRevisionTwoLabelKind | null;
  /** End exclusive. The best non-red window, or the best window when every window is red. */
  bestWindow: Readonly<{ startHour: number; endHour: number }> | null;
  noSafeWindow: boolean;
  windows: readonly SailingRevisionTwoWindow[];
  /** One entry per input hour, same order: the best composite of a window covering it, or null. */
  hourly: readonly Readonly<{ hour: number; score: number | null }>[];
}>;

type SailingSafetyRowKey = 'storm' | 'storm-forecast' | 'strong-wind' | 'gust-volatility' | 'visibility' | 'cold-exposure';

export type SailingHourSafetyRow = Readonly<{
  key: SailingSafetyRowKey;
  level: 'amber' | 'red' | 'unavailable';
  label: string;
}>;

type SailingRedHour = Pick<SailingSafetyHour, 'windKmh' | 'gustKmh' | 'weatherCode' | 'visibilityM'>;

function isThunderCode(code: number): boolean {
  return code >= 95 && code <= 99;
}

/**
 * The storm hazard row. A thunderstorm code is red. Storm risk (amber) is CAPE above 1000 J/kg
 * with a lifted index below -2, and CAPE above 1000 J/kg alone where the lifted index is missing
 * (owner action A-08, the recommended rule of docs/handover/WP-S5.md "Storm-input measurement",
 * as O-17 settled for SUP): 20 of the 38 venues never receive a lifted index from the provider, so
 * without it the amber could never fire there. It can only err towards amber (the pair needs the
 * same CAPE). Either known value on the stable side rules storm risk out; with CAPE missing and
 * the lifted index missing or unstable, while the weather code is known, the row is "Storm risk
 * unavailable", never clear. Storm risk reads amber with the weather code missing too (a real
 * hazard outranks a missing input, engines/core/day-safety.ts); the missing code itself is
 * stormForecastRow's.
 */
function stormRow(hour: Pick<SailingSafetyHour, 'weatherCode' | 'capeJkg' | 'liftedIndex'>): SailingHourSafetyRow | null {
  const { weatherCode, capeJkg, liftedIndex } = hour;
  if (weatherCode !== null && isThunderCode(weatherCode)) return { key: 'storm', level: 'red', label: 'Thunderstorm' };
  const stable = (capeJkg !== null && capeJkg <= SAILING_SAFETY_THRESHOLDS.stormRiskMinCapeJkg) ||
    (liftedIndex !== null && liftedIndex >= SAILING_SAFETY_THRESHOLDS.stormRiskMaxLiftedIndex);
  // Not stable with CAPE known means CAPE above the threshold: the pair, or CAPE alone (A-08).
  if (!stable && capeJkg !== null) return { key: 'storm', level: 'amber', label: 'Storm risk' };
  if (weatherCode === null || stable) return null;
  return { key: 'storm', level: 'unavailable', label: 'Storm risk unavailable' };
}

/**
 * The storm forecast row (owner decision O-11). A missing weather code cannot rule out a
 * thunderstorm, so the hour reads "Storm forecast unavailable" whatever CAPE and the lifted
 * index show; where they prove storm risk, stormRow's amber row stands beside it, so the hour
 * stays amber and the missing forecast is still disclosed.
 */
function stormForecastRow(hour: Pick<SailingSafetyHour, 'weatherCode'>): SailingHourSafetyRow | null {
  return hour.weatherCode === null ? { key: 'storm-forecast', level: 'unavailable', label: 'Storm forecast unavailable' } : null;
}

/**
 * The strong-wind row (SAIL-3). A missing gust takes the sustained wind (its neutral).
 * With the sustained wind missing a known gust still decides what it can prove (red or
 * amber by gust, or clear when even the gust is below the amber mean); otherwise the row
 * is unavailable, never clear.
 */
function strongWindRow(hour: Pick<SailingSafetyHour, 'windKmh' | 'gustKmh'>, category: BoatCategory): SailingHourSafetyRow | null {
  const ladder = SAILING_STRONG_WIND_LADDER[category];
  const red: SailingHourSafetyRow = { key: 'strong-wind', level: 'red', label: 'Too windy' };
  const amber: SailingHourSafetyRow = { key: 'strong-wind', level: 'amber', label: 'Strong wind' };
  const windKts = hour.windKmh === null ? null : toKnots(hour.windKmh);
  const gustKts = hour.gustKmh === null ? null : toKnots(hour.gustKmh);
  if (windKts === null) {
    if (gustKts === null) return { key: 'strong-wind', level: 'unavailable', label: 'Wind forecast unavailable' };
    if (gustKts >= ladder.redGustKts) return red;
    if (gustKts >= ladder.amberGustKts) return amber;
    // The sustained wind never exceeds its gust: below the amber mean both are clear.
    if (gustKts < ladder.amberMeanKts) return null;
    return { key: 'strong-wind', level: 'unavailable', label: 'Wind forecast unavailable' };
  }
  const gust = gustKts ?? windKts;
  if (windKts >= ladder.redMeanKts || gust >= ladder.redGustKts) return red;
  if (windKts >= ladder.amberMeanKts || gust >= ladder.amberGustKts) return amber;
  return null;
}

/**
 * Gust volatility, only in usable wind (SAIL-12); a missing gust is neutral (no volatility).
 * With the sustained wind missing, a gust below twice the usable wind rules volatility out
 * (a volatile hour needs at least 6 kt sustained and a gust of at least twice that); a
 * stronger gust cannot, so the row is unavailable, never clear.
 */
function gustVolatilityRow(hour: Pick<SailingSafetyHour, 'windKmh' | 'gustKmh'>): SailingHourSafetyRow | null {
  if (hour.gustKmh === null) return null;
  if (hour.windKmh === null) {
    return toKnots(hour.gustKmh) >= SAILING_SAFETY_THRESHOLDS.gustVolatilityRatio * SAILING_USABLE_WIND_KTS
      ? { key: 'gust-volatility', level: 'unavailable', label: 'Gust volatility unavailable' }
      : null;
  }
  if (toKnots(hour.windKmh) < SAILING_USABLE_WIND_KTS) return null;
  return hour.gustKmh / hour.windKmh >= SAILING_SAFETY_THRESHOLDS.gustVolatilityRatio
    ? { key: 'gust-volatility', level: 'amber', label: 'Severe gust volatility' }
    : null;
}

/**
 * The visibility row of the v2 safety module: model fog (model-fog.ts, accelerated rollout Phase E),
 * red below 500 m only in saturated air (a relative humidity of 94% or more, a dew-point depression
 * of about 1 C, or unknown), amber for any other visibility below 1,000 m (dry air included), and
 * "Visibility unavailable" when the visibility is missing.
 */
function visibilityRow(hour: Pick<SailingSafetyHour, 'visibilityM' | 'relativeHumidityPct'>): SailingHourSafetyRow | null {
  const fog = classifyModelFog(hour, SAILING_SAFETY_THRESHOLDS.visibilityRedBelowM);
  if (fog === 'unavailable') return { key: 'visibility', level: 'unavailable', label: 'Visibility unavailable' };
  if (fog === 'red') return { key: 'visibility', level: 'red', label: 'Poor visibility' };
  if (fog === 'amber') return { key: 'visibility', level: 'amber', label: 'Poor visibility' };
  return null;
}

/**
 * Whether the visibility alone makes an hour red for the revision 2 window rule (SAIL-6,
 * sailingHourIsRed): below 500 m, as registered. The window rule is part of the revision 2 score
 * formula (the best window and the "No safe window" label), which must not move before its shadow
 * registration, so it keeps the visibility-alone reading the safety row lowered in Phase E: a model
 * fog hour the safety row reads amber ("Poor visibility", which explains the exclusion) can still
 * keep a window out of the choice and give a day of such hours the "No safe window" label (gates
 * addendum A16; aligning the two is a formula change for a later revision).
 */
function formulaVisibilityRed(hour: Pick<SailingSafetyHour, 'visibilityM'>): boolean {
  return hour.visibilityM !== null && hour.visibilityM < SAILING_SAFETY_THRESHOLDS.visibilityRedBelowM;
}

/**
 * Cold rain or sleet (amber). A modifier (owner decision O-11): a missing apparent temperature
 * or precipitation is neutral and adds no row, so a gap in either never makes a day unavailable;
 * known values flag at the same thresholds.
 */
function coldExposureRow(hour: Pick<SailingSafetyHour, 'apparentTempC' | 'precipMm'>): SailingHourSafetyRow | null {
  const { apparentTempC, precipMm } = hour;
  if (apparentTempC === null || precipMm === null) return null;
  if (precipMm < SAILING_SAFETY_THRESHOLDS.coldExposureMinPrecipMm) return null;
  if (apparentTempC >= SAILING_SAFETY_THRESHOLDS.coldExposureApparentBelowC) return null;
  return { key: 'cold-exposure', level: 'amber', label: 'Cold exposure' };
}

/**
 * Per-hour v2 safety rows for a boat category (design 2.7; SAIL-3, SAIL-5, SAIL-12).
 * Levels are amber, red or unavailable only: an hour with no row is clear, and a
 * missing input that could hide a hazard gives an unavailable row, never clear.
 */
export function sailingHourSafetyRows(hour: SailingSafetyHour, category: BoatCategory): SailingHourSafetyRow[] {
  return [
    stormRow(hour), stormForecastRow(hour), strongWindRow(hour, category), gustVolatilityRow(hour), visibilityRow(hour), coldExposureRow(hour),
  ].filter((row): row is SailingHourSafetyRow => row !== null);
}

/**
 * Whether an hour carries a red row (SAIL-6 window choice). Reads only the inputs red rows
 * depend on (thunderstorm code, sustained wind and gust, visibility); a missing modifier
 * is never red.
 */
export function sailingHourIsRed(hour: SailingRedHour, category: BoatCategory): boolean {
  return formulaVisibilityRed(hour) ||
    [stormRow({ weatherCode: hour.weatherCode, capeJkg: null, liftedIndex: null }), strongWindRow(hour, category)]
      .some((row) => row?.level === 'red');
}

function inSectors(directionDeg: number, sectors: readonly (readonly [number, number])[]): boolean {
  return sectors.some(([start, end]) => (start <= end
    ? directionDeg >= start && directionDeg <= end
    : directionDeg >= start || directionDeg <= end));
}

function directionSamples(): number[] {
  const samples: number[] = [];
  for (let direction = 0; direction < 360; direction += R2_DIRECTION_STEP_DEG) samples.push(direction);
  return samples;
}

/**
 * The share of an offshore (not exposed) wind: 0 or 1 for a known direction, and the
 * direction-averaged share of the compass outside the exposed sectors when it is missing.
 */
function offshoreShare(windFromDeg: number | null, sectors: readonly (readonly [number, number])[]): number {
  if (windFromDeg !== null) return inSectors(windFromDeg, sectors) ? 0 : 1;
  const samples = directionSamples();
  return samples.filter((direction) => !inSectors(direction, sectors)).length / samples.length;
}

/** The revision 1 wind-over-tide response for one angle between the two headings. */
function windTideResponse(angleDeg: number, currentMs: number, avgWindKts: number): number {
  const rule = SAILING_REVISION_TWO_PARAMS.windAgainstTide;
  if (angleDeg > rule.opposedAboveDeg) {
    const oppositionStrength = (angleDeg - rule.opposedAboveDeg) / (180 - rule.opposedAboveDeg);
    const currentFactor = clamp(currentMs / 1.5, 0, 1);
    const windFactor = clamp(avgWindKts / 25, 0.3, 1);
    return -Math.round(oppositionStrength * currentFactor * windFactor * rule.maxPenalty);
  }
  if (angleDeg < rule.alignedBelowDeg) {
    return clamp(currentMs * 3 * ((rule.alignedBelowDeg - angleDeg) / rule.alignedBelowDeg), 0, rule.maxBonus);
  }
  return 0;
}

/**
 * Wind against tide (SAIL-1): the window's vector-mean current (a missing current is a zero
 * vector, its neutral) against the wind's heading. With no wind direction in the window the
 * response is averaged over every wind heading.
 */
function windAgainstTideAdjustment(hours: readonly SailingEngineHour[], windFromDeg: number | null, avgWindKts: number): number {
  const toRad = Math.PI / 180;
  let east = 0;
  let north = 0;
  for (const hour of hours) {
    if (hour.currentMs === null || hour.currentTowardsDeg === null) continue;
    east += hour.currentMs * Math.sin(hour.currentTowardsDeg * toRad);
    north += hour.currentMs * Math.cos(hour.currentTowardsDeg * toRad);
  }
  east /= hours.length;
  north /= hours.length;
  const currentMs = Math.hypot(east, north);
  if (!(currentMs > SAILING_REVISION_TWO_PARAMS.windAgainstTide.minCurrentMs)) return 0;
  const currentTowards = ((Math.atan2(east, north) / toRad) + 360) % 360;
  if (windFromDeg !== null) return windTideResponse(angularDiff((windFromDeg + 180) % 360, currentTowards), currentMs, avgWindKts);
  const samples = directionSamples();
  return samples.reduce((sum, windTowards) => sum + windTideResponse(angularDiff(windTowards, currentTowards), currentMs, avgWindKts), 0) / samples.length;
}

function presentMean(values: readonly (number | null)[]): number | null {
  const present = values.filter((value): value is number => value !== null);
  return present.length > 0 ? mean(present) : null;
}

function seaStateRevisionTwo(
  hours: readonly SailingEngineHour[],
  venueType: VenueType,
  sectors: readonly (readonly [number, number])[] | null,
  windFromDeg: number | null,
  avgWindKts: number,
): number | null {
  if (venueType === 'inland') return SAILING_REVISION_TWO_PARAMS.inlandSeaState;
  const waveHeight = presentMean(hours.map((hour) => hour.windWaveHeightM)) ??
    presentMean(hours.map((hour) => hour.waveHeightM)) ??
    presentMean(hours.map((hour) => hour.swellHeightM));
  if (waveHeight === null) return null;
  let baseScore = lerp(WAVE_HEIGHT_CURVE, waveHeight);

  const neutral = SAILING_REVISION_TWO_PARAMS.neutral;
  const swellHeight = mean(hours.map((hour) => hour.swellHeightM ?? neutral.steepSwellHeightM));
  const swellPeriod = mean(hours.map((hour) => hour.swellPeriodS ?? neutral.swellPeriodS));
  if (swellHeight > 0.8) {
    if (swellPeriod < 5) {
      baseScore -= 20;
    } else if (swellPeriod < 7) {
      baseScore -= clamp((swellHeight - 0.8) * 15 * ((7 - swellPeriod) / 2), 0, 18);
    }
  }

  baseScore += windAgainstTideAdjustment(hours, windFromDeg, avgWindKts);

  if (sectors && waveHeight > 0.3) {
    const fetchBonus = clamp(Math.round((100 - baseScore) * 0.3), 0, 18);
    baseScore += fetchBonus * offshoreShare(windFromDeg, sectors);
  }

  if (venueType === 'estuary' && waveHeight < 1.0) baseScore *= 1.08;
  return clamp(Math.round(baseScore), 0, 100);
}

/**
 * The pressure trend in hPa per hour (SAIL-13: provisional, weight 0 in revision 2, shown for
 * display): the last present reading minus the first over their hour gap; null with fewer
 * than two readings at distinct hours. Carriers may use it to show hPa/h beside a revision 1
 * window's pressure factor (docs/handover/WP-S5.md).
 */
export function sailingPressureTrendHpaPerHour(hours: readonly Readonly<{ hour: number; pressureHpa: number | null }>[]): number | null {
  const readings = hours.filter((hour): hour is Readonly<{ hour: number; pressureHpa: number }> => hour.pressureHpa !== null);
  if (readings.length < 2) return null;
  const first = readings[0];
  const last = readings[readings.length - 1];
  return last.hour === first.hour ? null : (last.pressureHpa - first.pressureHpa) / (last.hour - first.hour);
}

function scoreWindowRevisionTwo(
  hours: readonly SailingEngineHour[],
  category: BoatCategory,
  venueType: VenueType,
  sectors: readonly (readonly [number, number])[] | null,
): SailingRevisionTwoWindow {
  const params = SAILING_REVISION_TWO_PARAMS;
  // Window hours are available: the driver is present in every one.
  const windKmh = hours.map((hour) => hour.windKmh ?? 0);
  const gustKmh = hours.map((hour, index) => hour.gustKmh ?? windKmh[index]);
  const windKts = windKmh.map(toKnots);
  const gustKts = gustKmh.map(toKnots);
  const avgWindKts = mean(windKts);
  const maxWindKts = Math.max(...windKts);
  const minWindKts = Math.min(...windKts);
  const gustRatios = windKmh.map((sustained, index) => (sustained < 2 ? 1.0 : gustKmh[index] / sustained));
  const avgGustRatio = mean(gustRatios);
  const maxGustRatio = Math.max(...gustRatios);
  const directions = hours.flatMap((hour) => (hour.windFromDeg === null ? [] : [hour.windFromDeg]));
  const windFromDeg = directions.length > 0 ? circularMean(directions) : null;

  // Wind viability (SAIL-4 for high performance; no 80 m shear term).
  const curve = category === 'club' ? params.viability.club : params.viability.high_performance;
  const variabilityRatio = avgWindKts > 2 ? (maxWindKts - minWindKts) / avgWindKts : 0;
  const variabilityPenalty = variabilityRatio > 0.3 ? clamp((variabilityRatio - 0.3) * 30, 0, 20) : 0;
  const windQuality = clamp(Math.round(lerp(curve, avgWindKts) - variabilityPenalty), 0, 100);

  // Gust stability, with the offshore penalty direction-averaged when the direction is missing.
  let gustFactor = 100;
  if (avgWindKts >= 3) {
    const baseScore = lerp(GUST_RATIO_CURVE, avgGustRatio);
    const highWindThreshold = category === 'club' ? 18 : 24;
    let amplifiedScore = baseScore;
    if (avgWindKts > highWindThreshold) {
      const amplification = 1.0 + clamp((avgWindKts - highWindThreshold) / 10, 0, 0.5);
      amplifiedScore = clamp(100 - (100 - baseScore) * amplification, 0, 100);
    }
    const spikePenalty = maxGustRatio > 2.0 && maxGustRatio > avgGustRatio + 0.3 ? clamp((maxGustRatio - 2.0) * 12, 0, 15) : 0;
    const offshorePenalty = sectors ? 6 * offshoreShare(windFromDeg, sectors) : 0;
    gustFactor = clamp(Math.round(amplifiedScore - spikePenalty - offshorePenalty), 0, 100);
  }

  // Wind consistency needs two directions; otherwise the term drops out.
  let windConsistency: number | null = null;
  if (windFromDeg !== null && directions.length >= 2) {
    let baseScore = lerp(WIND_DIR_STD_CURVE, circularStdDev(directions));
    if (sectors && !inSectors(windFromDeg, sectors)) {
      baseScore -= Math.round(10 * clamp((15 - avgWindKts) / 10, 0.3, 1.0));
    }
    windConsistency = clamp(Math.round(baseScore), 0, 100);
  }

  const seaState = seaStateRevisionTwo(hours, venueType, sectors, windFromDeg, avgWindKts);
  const precipitation = clamp(Math.round(lerp(RAIN_CURVE, mean(hours.map((hour) => hour.precipMm ?? params.neutral.precipMm)))), 0, 100);

  // Visibility once (SAIL-15): the stronger of the haze curve and the sighting blend.
  const visibilities = hours.map((hour) => hour.visibilityM ?? params.neutral.visibilityM);
  const minVisibility = Math.min(...visibilities);
  const avgVisibility = mean(visibilities);
  let hazeScore = lerp(VISIBILITY_CURVE, minVisibility);
  if (avgVisibility < 5000 && hazeScore > 60) hazeScore -= clamp((5000 - avgVisibility) / 500, 0, 10);
  const visibility = clamp(Math.round(hazeScore), 0, 100);
  const hazeFactor = Math.pow(Math.max(0.01, visibility / 100), params.visibility.power);
  const sighting = clamp(minVisibility / params.visibility.sightingFullM, 0, 1);
  const visibilityFactor = Math.min(hazeFactor, sighting * sighting * (3 - 2 * sighting));

  // Additive quality: terms with data, weights renormalised; pressure carries weight 0 (SAIL-13).
  const weights = params.qualityWeights;
  const candidates: readonly (readonly [number, number | null])[] = [
    [weights.windConsistency, windConsistency],
    [weights.seaState, seaState],
    [weights.precipitation, precipitation],
  ];
  const terms = candidates.filter((term): term is readonly [number, number] => term[1] !== null && term[0] > 0);
  const weightSum = terms.reduce((sum, [weight]) => sum + weight, 0);
  const qualityPoints = terms.reduce((sum, [weight, value]) => sum + weight * value, 0) / weightSum;

  const windViability = windQuality / 100;
  const gustStability = Math.pow(Math.max(0.01, gustFactor / 100), params.gustPower);
  const composite = clamp(composeScore(SAILING_WINDOW_CHAIN, {
    windViability,
    gustStability,
    quality: qualityPoints,
    visibility: visibilityFactor,
  }).compressed, 0, 100);

  const pressureTrendHpaPerHour = sailingPressureTrendHpaPerHour(hours);

  return {
    startHour: hours[0].hour,
    endHour: hours[hours.length - 1].hour + 1,
    composite,
    red: hours.some((hour) => sailingHourIsRed(hour, category)),
    meanWindKts: avgWindKts,
    maxGustKts: Math.max(...gustKts),
    windQuality,
    gustFactor,
    windConsistency,
    seaState,
    precipitation,
    visibility,
    windViability,
    gustStability,
    qualityPoints,
    visibilityFactor,
    pressureTrendHpaPerHour,
  };
}

/** The flat top of the revision 1 viability curves (knots), for the SAIL-3 display rule on revision 1 days. */
const REVISION_ONE_OPTIMUM_KNOTS = Object.freeze({ club: Object.freeze([14, 16] as const), high_performance: Object.freeze([18, 24] as const) });

/**
 * Whether a window is overpowered (SAIL-3): its mean wind is above the category's viability
 * optimum and the wind gate has removed at least half of the score (`windQuality`, the
 * viability sub-score, below 50). Revision 2 labels such a day "Too windy". Design 6.2 lets
 * carriers apply the same display rule to revision 1 days under safety policy v2: pass
 * revision 1, the best window's mean sustained wind and SailingScore.subScores.windQuality,
 * and the revision 1 curve's optimum applies.
 */
export function sailingWindowOverpowered(
  window: Readonly<{ meanWindKts: number; windQuality: number }>,
  category: BoatCategory,
  revision: 1 | 2,
): boolean {
  const [, high] = (revision === 1 ? REVISION_ONE_OPTIMUM_KNOTS : SAILING_REVISION_TWO_PARAMS.optimumKnots)[category];
  return window.meanWindKts > high && window.windQuality < SAILING_REVISION_TWO_PARAMS.labels.overpoweredBelowViability;
}

/**
 * The design 6.2 "Too windy" display label for a revision 1 window under safety policy v2
 * (SAIL-3): the stored label, unless the window is overpowered by the revision 1 rule of
 * sailingWindowOverpowered. `windKmh` are the window's sustained 10 m winds and `windQuality`
 * is SailingScore.subScores.windQuality. Carriers apply it (docs/handover/WP-S5.md), never the
 * revision 1 wrap, so revision 1 outputs and hashes stay as they are.
 */
export function sailingRevisionOneDisplayLabel(
  label: string,
  windKmh: readonly number[],
  windQuality: number,
  category: BoatCategory,
): string {
  if (windKmh.length === 0) return label;
  return sailingWindowOverpowered({ meanWindKts: mean(windKmh.map(toKnots)), windQuality }, category, 1) ? 'Too windy' : label;
}

function revisionTwoLabel(
  score: number,
  best: SailingRevisionTwoWindow,
  noSafeWindow: boolean,
  category: BoatCategory,
): readonly [SailingRevisionTwoLabel, SailingRevisionTwoLabelKind] {
  // Review decision 14 (SAIL-6): every window red, whatever the red row, keeps the number and
  // reads "No safe window"; a red strong-wind row still reads "Too windy" among the flags.
  if (noSafeWindow) return ['No safe window', 'no-safe-window'];
  const [low, high] = SAILING_REVISION_TWO_PARAMS.optimumKnots[category];
  const labels = SAILING_REVISION_TWO_PARAMS.labels;
  if (sailingWindowOverpowered(best, category, 2)) return ['Too windy', 'overpowered'];
  // SAIL-3 (owner decision O-11, WP-S5 option b): in the bottom bands, a best window above the
  // optimum whose mean wind reaches the ladder's amber rung is overpowered, not "Poor". The amber
  // rung (rather than any viability below 100) keeps "Poor" on a fog or rain day in good wind.
  if (
    score < labels.overpoweredBottomBandsBelowScore &&
    best.meanWindKts > high &&
    best.meanWindKts >= SAILING_STRONG_WIND_LADDER[category].amberMeanKts
  ) {
    return ['Too windy', 'overpowered'];
  }
  // The shared platform bands of assignLabel above.
  if (score >= 90) return ['Epic', 'quality'];
  if (score >= 75) return ['Firing', 'quality'];
  if (score >= 65) return ['Great', 'quality'];
  if (score >= 50) return ['Good', 'quality'];
  if (score >= 35) return ['Fair', 'quality'];
  if (score >= 20) return ['Poor', 'quality'];
  // "Becalmed" means light wind holds the score down; anything else at the bottom is "Poor".
  const underpowered = best.meanWindKts < low && best.windQuality < labels.underpoweredBelowViability;
  return [underpowered ? 'Becalmed' : 'Poor', 'quality'];
}

/**
 * Revision 2 sailing day for one boat category. `hours` are the stored local hours in order
 * with presence resolved (SailingEngineHour); the result holds one hourly entry per input
 * hour. `formula` must be revision 2 (SAILING_REVISION_TWO_FORMULA): revision 1 is
 * scoreSailing and scoreSailingDual, unchanged.
 */
export function scoreSailingRevisionTwo(
  hours: readonly SailingEngineHour[],
  category: BoatCategory,
  venueType: VenueType,
  exposedSectors: readonly (readonly [number, number])[] | null,
  formula: SailingFormula,
): SailingRevisionTwoDay {
  if (leafFormulaRevision(formula) !== 2) {
    throw new RangeError(`scoreSailingRevisionTwo serves revision 2, not ${leafFormulaRevision(formula)}`);
  }
  const rule = SAILING_REVISION_TWO_PARAMS.window;
  const sectors = exposedSectors && exposedSectors.length > 0 ? exposedSectors : null;
  const pool = hours.filter((hour) => hour.windKmh !== null && hour.isDay && hour.hour >= rule.fromHour && hour.hour < rule.toHour);
  const windows = consecutiveHourlyWindows(pool, rule.hours).map((window) => scoreWindowRevisionTwo(window, category, venueType, sectors));
  const hourly = hours.map((hour) => {
    const covering = windows.filter((window) => hour.hour >= window.startHour && hour.hour < window.endHour);
    return { hour: hour.hour, score: covering.length > 0 ? Math.max(...covering.map((window) => window.composite)) : null };
  });
  if (windows.length === 0) {
    return { score: null, label: null, labelKind: null, bestWindow: null, noSafeWindow: false, windows, hourly };
  }
  const safe = windows.filter((window) => !window.red);
  const noSafeWindow = safe.length === 0;
  const candidates = noSafeWindow ? windows : safe;
  // First best on ties, as revision 1.
  const best = candidates.reduce((top, window) => (window.composite > top.composite ? window : top));
  const poolAverage = mean(windows.map((window) => window.composite));
  const blend = SAILING_REVISION_TWO_PARAMS.blend;
  const score = clamp(best.composite * blend.bestWindow + poolAverage * blend.poolAverage, 0, 100);
  const [label, labelKind] = revisionTwoLabel(score, best, noSafeWindow, category);
  return {
    score,
    label,
    labelKind,
    bestWindow: { startHour: best.startHour, endHour: best.endHour },
    noSafeWindow,
    windows,
    hourly,
  };
}

// ---------------------------------------------------------------------------
// Location definitions
// ---------------------------------------------------------------------------

export interface SailingLocation {
  name: string;
  slug: string;
  latitude: number;
  longitude: number;
  venueType: VenueType;
  region: string;
  notes: string;
  exposedSectors?: [number, number][];
}

export const SAILING_LOCATIONS: SailingLocation[] = [
  // UK & Ireland - Inland
  { name: 'Rutland Water', slug: 'rutland-water', latitude: 52.64, longitude: -0.64, venueType: 'inland', region: 'East Midlands', notes: 'Largest man-made lake in England. Major open meeting venue.' },
  { name: 'Grafham Water', slug: 'grafham-water', latitude: 52.29, longitude: -0.32, venueType: 'inland', region: 'Cambridgeshire', notes: 'Big open reservoir. National championship venue.' },
  { name: 'Carsington Water', slug: 'carsington-water', latitude: 53.05, longitude: -1.63, venueType: 'inland', region: 'Derbyshire', notes: 'Popular Midlands venue. Good wind exposure for a reservoir.' },
  { name: 'Welsh Harp', slug: 'welsh-harp', latitude: 51.57, longitude: -0.22, venueType: 'inland', region: 'London', notes: "London's most accessible reservoir sailing club." },
  { name: 'Pitsford Water', slug: 'pitsford-water', latitude: 52.34, longitude: -0.87, venueType: 'inland', region: 'Northamptonshire', notes: 'Strong club racing scene. Well-exposed reservoir.' },
  { name: 'Llandegfedd Reservoir', slug: 'llandegfedd', latitude: 51.70, longitude: -3.00, venueType: 'inland', region: 'South Wales', notes: 'Welsh reservoir. Good western wind exposure.' },

  // UK & Ireland - Estuary
  { name: 'Chichester Harbour', slug: 'chichester-harbour', latitude: 50.79, longitude: -0.89, venueType: 'estuary', region: 'West Sussex', notes: 'Huge fleet racing. Multiple clubs. Tidal.', exposedSectors: [[160, 240]] },
  { name: 'Camel Estuary', slug: 'camel-estuary', latitude: 50.55, longitude: -4.93, venueType: 'estuary', region: 'North Cornwall', notes: 'Rock SC, Padstow. Classic estuary racing. Strong tides.', exposedSectors: [[270, 350]] },
  { name: 'Exe Estuary', slug: 'exe-estuary', latitude: 50.62, longitude: -3.43, venueType: 'estuary', region: 'Devon', notes: 'Lympstone, Starcross clubs. Strong tidal flow.', exposedSectors: [[130, 210]] },
  { name: 'Hamble River', slug: 'hamble-river', latitude: 50.87, longitude: -1.31, venueType: 'estuary', region: 'Hampshire', notes: 'One of the densest concentrations of sailing globally.', exposedSectors: [[150, 230]] },
  { name: 'Menai Strait', slug: 'menai-strait', latitude: 53.18, longitude: -4.17, venueType: 'estuary', region: 'North Wales', notes: 'Plas Menai national centre. Strong tidal racing.', exposedSectors: [[20, 70], [200, 250]] },
  { name: 'Burnham-on-Crouch', slug: 'burnham-on-crouch', latitude: 51.63, longitude: 0.81, venueType: 'estuary', region: 'Essex', notes: 'Historic racing venue. Royal Corinthian, Royal Burnham.', exposedSectors: [[30, 150]] },

  // UK & Ireland - Coastal Open
  { name: 'Hayling Island', slug: 'hayling-island', latitude: 50.78, longitude: -0.97, venueType: 'coastal_open', region: 'Hampshire', notes: "Arguably the UK's premier dinghy racing venue. Open water.", exposedSectors: [[150, 250]] },
  { name: 'Weymouth & Portland', slug: 'weymouth-portland', latitude: 50.57, longitude: -2.44, venueType: 'coastal_open', region: 'Dorset', notes: '2012 Olympic sailing venue. Portland Harbour + open bay.', exposedSectors: [[90, 240]] },
  { name: 'Whitstable', slug: 'whitstable', latitude: 51.37, longitude: 1.03, venueType: 'coastal_open', region: 'Kent', notes: 'Thames estuary opens to North Sea. Good east coast venue.', exposedSectors: [[300, 80]] },
  { name: 'Pwllheli', slug: 'pwllheli', latitude: 52.88, longitude: -4.40, venueType: 'coastal_open', region: 'North Wales', notes: 'Welsh National Sailing Academy. Championship venue.', exposedSectors: [[150, 260]] },
  { name: 'Largs', slug: 'largs', latitude: 55.79, longitude: -4.87, venueType: 'coastal_open', region: 'Firth of Clyde', notes: 'Scottish Sailing Institute. Major Scottish venue.', exposedSectors: [[220, 330]] },
  { name: 'Dun Laoghaire', slug: 'dun-laoghaire', latitude: 53.29, longitude: -6.13, venueType: 'coastal_open', region: 'Dublin Bay', notes: 'Irish Sea coverage. Large club racing scene.', exposedSectors: [[20, 160]] },

  // Europe
  { name: 'Fornells Bay', slug: 'fornells-bay', latitude: 40.05, longitude: 4.13, venueType: 'coastal_open', region: 'Menorca, Spain', notes: 'Minorca Sailing. Enclosed bay with reliable thermal winds.', exposedSectors: [[330, 60]] },
  { name: 'Palma de Mallorca', slug: 'palma-mallorca', latitude: 39.55, longitude: 2.63, venueType: 'coastal_open', region: 'Mallorca, Spain', notes: 'Trofeo Princesa Sofia. World-class Olympic classes venue.', exposedSectors: [[150, 270]] },
  { name: 'Lake Garda', slug: 'lake-garda', latitude: 45.87, longitude: 10.84, venueType: 'inland', region: 'Trentino, Italy', notes: 'Riva del Garda / Torbole. Famous Ora and Peler thermal winds.' },
  { name: 'Hyères', slug: 'hyeres', latitude: 43.10, longitude: 6.15, venueType: 'coastal_open', region: 'Var, France', notes: 'Semaine Olympique Française. Reliable Mistral and thermal winds.', exposedSectors: [[120, 240]] },
  { name: 'La Rochelle', slug: 'la-rochelle', latitude: 46.16, longitude: -1.15, venueType: 'coastal_open', region: 'Charente-Maritime, France', notes: 'Major Atlantic French sailing centre. Strong tidal range.', exposedSectors: [[200, 310]] },
  { name: 'Kiel', slug: 'kiel', latitude: 54.33, longitude: 10.15, venueType: 'coastal_open', region: 'Schleswig-Holstein, Germany', notes: 'Kieler Woche. One of the largest sailing events globally.', exposedSectors: [[0, 90]] },
  { name: 'Medemblik', slug: 'medemblik', latitude: 52.77, longitude: 5.10, venueType: 'inland', region: 'North Holland, Netherlands', notes: 'Regatta Centre Medemblik. IJsselmeer. Major European championship venue.' },
  { name: 'Aarhus', slug: 'aarhus', latitude: 56.15, longitude: 10.22, venueType: 'coastal_open', region: 'Jutland, Denmark', notes: '2018 World Championships host. Aarhus Bay.', exposedSectors: [[30, 150]] },

  // North America
  { name: 'Annapolis', slug: 'annapolis', latitude: 38.97, longitude: -76.48, venueType: 'estuary', region: 'Maryland, USA', notes: 'US Naval Academy. Chesapeake Bay. Sailing capital of the US.', exposedSectors: [[90, 220]] },
  { name: 'Newport', slug: 'newport-ri', latitude: 41.49, longitude: -71.33, venueType: 'coastal_open', region: 'Rhode Island, USA', notes: 'New York Yacht Club harbour. Strong currents in Narragansett Bay.', exposedSectors: [[150, 270]] },
  { name: 'Biscayne Bay', slug: 'biscayne-bay', latitude: 25.73, longitude: -80.17, venueType: 'coastal_open', region: 'Miami, Florida, USA', notes: 'Year-round sailing. Olympic classes winter training base.', exposedSectors: [[90, 200]] },
  { name: 'San Francisco Bay', slug: 'san-francisco-bay', latitude: 37.82, longitude: -122.38, venueType: 'coastal_open', region: 'California, USA', notes: 'St Francis YC. Strong afternoon westerlies, fierce tidal currents.', exposedSectors: [[240, 330]] },
  { name: 'Columbia River Gorge', slug: 'columbia-gorge', latitude: 45.70, longitude: -121.80, venueType: 'inland', region: 'Oregon/Washington, USA', notes: 'Legendary wind funnel. 20+ knots daily in summer.' },
  { name: 'Lake Ontario, Kingston', slug: 'kingston-ontario', latitude: 44.23, longitude: -76.48, venueType: 'inland', region: 'Ontario, Canada', notes: 'CORK regatta. One of the largest freshwater sailing events globally.' },

  // Australia & New Zealand
  { name: 'Sydney Harbour', slug: 'sydney-harbour', latitude: -33.85, longitude: 151.24, venueType: 'coastal_open', region: 'New South Wales, Australia', notes: 'Double Bay SC, Woollahra SC. Strong NE sea breeze in summer.', exposedSectors: [[0, 120]] },
  { name: 'Belmont', slug: 'belmont-lake-macquarie', latitude: -33.04, longitude: 151.66, venueType: 'inland', region: 'New South Wales, Australia', notes: 'Lake Macquarie. Largest saltwater lake in the southern hemisphere.' },
  { name: 'Sandringham', slug: 'sandringham-melbourne', latitude: -37.95, longitude: 145.00, venueType: 'coastal_open', region: 'Victoria, Australia', notes: 'Sandringham YC. Port Phillip Bay. Strong Australian dinghy scene.', exposedSectors: [[280, 60]] },
  { name: 'Auckland, Waitemata', slug: 'auckland-waitemata', latitude: -36.84, longitude: 174.77, venueType: 'coastal_open', region: 'Auckland, New Zealand', notes: 'Royal Akarana YC. City of Sails. SailGP venue.', exposedSectors: [[300, 80]] },

  // Southern Africa
  { name: 'Table Bay', slug: 'table-bay-cape-town', latitude: -33.90, longitude: 18.44, venueType: 'coastal_open', region: 'Cape Town, South Africa', notes: 'Royal Cape YC. Legendary SE trade winds (Cape Doctor).', exposedSectors: [[270, 30]] },
  { name: 'Vaal Dam', slug: 'vaal-dam', latitude: -26.87, longitude: 28.12, venueType: 'inland', region: 'Gauteng, South Africa', notes: 'Largest inland sailing venue in South Africa. Strong afternoon thermals.' },
];
